import Relay from 'react-relay';
import { fromGlobalId } from 'graphql-relay';

const uuidRegex = /[0-9a-fA-F\-]{30,40}/;

function normalizeSnapshotEntityId(id?: string | null): string | null {
    if (!id) {
        return null;
    }

    if (uuidRegex.test(id)) {
        return id;
    }

    try {
        const decodedId = fromGlobalId(id);
        return decodedId.id || id;
    } catch (e) {
        return id;
    }
}

function getPreferredSnapshotEntityId(entity?: { id?: string | null; realId?: string | null } | null): string | null {
    if (!entity) {
        return null;
    }

    return entity.realId || normalizeSnapshotEntityId(entity.id);
}

export interface SetExpectedOutputMutationProps {
    testInvocation: {
        id: string;
        realId?: string;
        version: number;
        expectedOutput?: any;
    };
    expectedOutput: any;
    testRunId?: string | null;
}

export interface SetExpectedOutputMutationResponse {
    testInvocation: {
        id: string;
        version: number;
        expectedOutput?: any;
    } | null;
    success: boolean;
    conflict: boolean;
    error: string | null;
}

export default class SetExpectedOutputMutation extends Relay.Mutation<SetExpectedOutputMutationProps, SetExpectedOutputMutationResponse> {
    private mutationProps: SetExpectedOutputMutationProps;

    constructor(props: SetExpectedOutputMutationProps) {
        super(props as any);
        this.mutationProps = props;
    }

    getMutation() {
        return Relay.QL`mutation {setExpectedOutput}`;
    }

    getFatQuery() {
        return Relay.QL`
            fragment on SetExpectedOutputPayload {
                testInvocation {
                    id
                    expectedOutput
                    version
                }
                success
                conflict
                error
            }
        `;
    }

    getConfigs() {
        return [{
            type: 'FIELDS_CHANGE',
            fieldIDs: {
                testInvocation: this.mutationProps.testInvocation.id,
            },
        }, {
            // FIELDS_CHANGE only selects tracked entity fields; the callbacks also need the
            // acknowledgement fields, which are never tracked in the store.
            type: 'REQUIRED_CHILDREN',
            children: [Relay.QL`
                fragment on SetExpectedOutputPayload {
                    success
                    conflict
                    error
                }
            `],
        }];
    }

    getVariables() {
        const testInvocationId = getPreferredSnapshotEntityId(this.mutationProps.testInvocation) || this.mutationProps.testInvocation.id;
        return {
            testInvocationId,
            expectedOutput: JSON.stringify(this.mutationProps.expectedOutput),
            version: this.mutationProps.testInvocation.version,
            testRunId: this.mutationProps.testRunId || null,
        };
    }

    getOptimisticResponse() {
        const { testInvocation, expectedOutput } = this.mutationProps;

        return {
            testInvocation: {
                id: testInvocation.id,
                version: testInvocation.version + 1,
                expectedOutput: expectedOutput,
            },
            success: true,
            conflict: false,
            error: null,
        };
    }
}
